import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

export default function DealCriteriaPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-8 p-4 py-12">
      <Link to="/resources" className="text-amber-600 hover:underline">
        &larr; Back to Resources
      </Link>
      <h1 className="text-3xl font-bold">What Disqualifies a Deal in 5 Minutes</h1>

      <Card>
        <CardContent className="p-6">
          <p className="text-lg">
            Not every property is worth buying. Having strict criteria prevents you from talking yourself into a bad
            deal because you're excited about owning real estate. Here's what Fairmeadow Capital looks for and what
            makes us pass.
          </p>
        </CardContent>
      </Card>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Must-Have Criteria</h2>
        <p className="text-muted-foreground">If a property doesn't meet all of these, we don't underwrite further.</p>

        <div className="space-y-3">
          {[
            {
              criteria: 'Cash-on-cash return: 8%+ from day one',
              why: "We don't rely on appreciation or rent growth to make a deal work. The returns need to exist on the current rent roll.",
            },
            {
              criteria: 'DSCR: 1.25x minimum',
              why: 'The property must comfortably cover its debt with room for unexpected expenses. Below 1.25x creates too little margin.',
            },
            {
              criteria: 'Cap rate: 8%+ (Cleveland small multifamily)',
              why: 'Ensures the property generates meaningful income relative to its price, independent of financing.',
            },
            {
              criteria: 'Located in target Cleveland submarkets',
              why: 'We invest where we know the rental demand, tenant base, comparable sales, and property management landscape.',
            },
            {
              criteria: '2-12 unit residential',
              why: 'Small enough for residential financing (2-4 units) or manageable commercial financing. Large enough for real cash flow.',
            },
            {
              criteria: 'Existing rental income or clear lease-up path',
              why: 'We prefer occupied properties generating income at close. If vacant, we need strong comparable rents and a clear rehab-to-lease timeline.',
            },
          ].map((item) => (
            <Card key={item.criteria}>
              <CardContent className="p-4">
                <p className="font-semibold">{item.criteria}</p>
                <p className="text-muted-foreground mt-1 text-sm">{item.why}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Separator />

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Preferred Characteristics</h2>
        <p className="text-muted-foreground">These make a deal more attractive but aren't disqualifying if absent.</p>

        <div className="grid gap-3 md:grid-cols-2">
          {[
            'Separate utilities (tenants pay own heat/electric)',
            'Below-market rents with upside after light rehab',
            'Stable, long-term tenants in place',
            'Brick or block construction',
            'Roof, HVAC, and water heaters under 10 years old',
            'Off-street parking',
            'Near public transit or major employers',
            'Seller-motivated (estate sale, tired landlord, relocation)',
          ].map((pref) => (
            <Card key={pref}>
              <CardContent className="p-3">
                <p className="flex gap-2 text-sm">
                  <span className="shrink-0 text-amber-500">&#10003;</span> {pref}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Separator />

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">Automatic Disqualifiers</h2>

        <div className="space-y-2">
          {[
            {
              flag: 'Foundation issues',
              note: 'Structural problems are unpredictable and expensive. Not worth the risk on a cash-flow play.',
            },
            {
              flag: 'Environmental contamination',
              note: 'Lead paint is manageable. Underground storage tanks, mold throughout, or asbestos remediation are not.',
            },
            {
              flag: 'The deal only works with appreciation',
              note: "If you need the property to go up in value to make money, it's speculation, not investing.",
            },
            {
              flag: 'Negative cash flow in year one',
              note: "We don't subsidize properties from other income. Every property must carry itself.",
            },
            {
              flag: 'High crime, no rental demand',
              note: "Cheap properties in areas where tenants don't want to live aren't deals. They're traps.",
            },
            { flag: "Seller won't allow inspection", note: "No exceptions. If we can't inspect, we don't buy." },
          ].map((item) => (
            <Card key={item.flag}>
              <CardContent className="p-4">
                <p className="flex gap-2 font-semibold">
                  <span className="shrink-0 text-red-400">&#x2717;</span> {item.flag}
                </p>
                <p className="text-muted-foreground mt-1 ml-6 text-sm">{item.note}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card>
        <CardContent className="p-6 text-center">
          <p className="mb-2 text-lg font-semibold">Run the numbers on a deal</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/resources/calculators/cash-on-cash" className="text-amber-600 hover:underline">
              Cash-on-Cash Calculator &rarr;
            </Link>
            <Link to="/resources/calculators/dscr" className="text-amber-600 hover:underline">
              DSCR Calculator &rarr;
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
